import http, { RequestConf } from '@/utils/http'

import {
  AddEndpointToMeshReq,
  AddUserSshPubKeyReq,
  CreateDeploymentReq,
  CreateInstanceReq,
  CreateSnapshotReq,
  DeleteEndpointFromMeshReq,
  DeleteInstanceReq,
  DeleteMeshReq,
  DeleteSnapshotReq,
  EduVerifyReq,
  GetAttachmentsReq,
  GetBriefMonitorDataReq,
  GetDeploymentListReq,
  GetGatewayListReq,
  GetImageListReq,
  GetInstanceListReq,
  GetMachineListReq,
  GetMeshEndPointListReq as GetMeshEndpointListReq,
  GetMeshEndpointNodesReq as GetMeshEndpointCandidatesReq,
  GetMeshListReq,
  GetMonitorDataReq,
  GetNATListReq,
  GetOrdersReq,
  GetScaleMetricListReq,
  GetScaleSpecListReq,
  GetScaleSpecReq,
  GetSnapListReq,
  Pagi,
  ResumeSnapshotReq,
  UpdateAttachmentsReq,
  UpdateDeploymentReq,
  UpdateInstancePortReq,
  UpdateMeshReq,
} from './type-request'
import {
  GetAccountRes,
  GetBriefMonitorDataRes,
  GetDeploymentBillRes,
  GetDeploymentListRes,
  GetDomainListRes,
  GetExchangeRateRes,
  GetGatewayListRes,
  GetImageListRes,
  GetInstanceListRes,
  GetItemConsumeRes,
  GetMachineListRes,
  GetMeshEndPointListRes as GetMeshEndpointListRes,
  GetMeshEndpointNodesRes as GetMeshEndpointCandidatesRes,
  GetMeshListRes,
  GetNATListRes,
  GetScaleMetricListRes,
  GetScaleSpecListRes,
  GetScaleSpecRes,
  GetSnapListRes,
  GetUserSshPubKeyRes,
} from './type-response'
import { ServerAttachmentInfo, ServerBillInfo, ServerUserInfo } from './type-server'

export function get<R = any, D = any>(path: TemplateStringsArray) {
  return (data?: D, config: RequestConf = {}) => http.get<R>(path[0], { ...config, params: data })
}

export function post<R = any, D = any>(path: TemplateStringsArray) {
  return (data?: D, config: RequestConf = {}) => http.post<R>(path[0], data, config)
}

export function put<R = any, D = any>(path: TemplateStringsArray) {
  return (data?: D, config: RequestConf = {}) => http.put<R>(path[0], data, config)
}

export function del<R = any, D = any>(path: TemplateStringsArray) {
  return (data?: D, config: RequestConf = {}) =>
    http.delete<R>(path[0], { ...config, params: data })
}

export function patch<R = any, D = any>(path: TemplateStringsArray) {
  return (data?: D, config: RequestConf = {}) => http.patch<R>(path[0], data, config)
}

export const API = {
  login: post<{ token: string }, { name: string; password: string }>`/login`,
  getUserInfo: get<ServerUserInfo>`/user`,
  getAccount: get<GetAccountRes>`/user/account`,
  getDomainList: get<GetDomainListRes>`/domain/list`,
  updatePassword: post<
    any,
    { name: string; ori_password: string; password: string }
  >`/reset_password`,

  getMachineList: get<GetMachineListRes, GetMachineListReq>`/machines`,
  getImageList: get<GetImageListRes, GetImageListReq>`/images`,
  getSnapList: get<GetSnapListRes, GetSnapListReq>`/snapshots`,
  getMeshList: get<GetMeshListRes, GetMeshListReq>`/meshes`,
  getMeshEndpointList: get<GetMeshEndpointListRes, GetMeshEndpointListReq>`/mesh/endpoints`,
  getNodeList: get<GetInstanceListRes, GetInstanceListReq>`/instances`,
  getNodeBill: get<{ bills: ServerBillInfo[] }, { request_id: string }>`/instance/bill`,
  getUserBills: get`/user/bills`,
  getGatewayList: get<GetGatewayListRes, GetGatewayListReq>`/gateway/list`,
  getDeploymentList: get<GetDeploymentListRes, GetDeploymentListReq>`/deployment/list`,

  payment: post<
    { url?: string; paymentCode?: string; clientSecret?: string },
    { fee: number; type: number; return_url?: string }
  >`/payment`,
  capture: post<{ code: string }>`/payment/paypal/capture`,

  getOrders: get<any, GetOrdersReq>`/orders`,
  getItems: get`/items`,
  purchase: post<any, { item_id: number; modify?: boolean; auto_renew?: boolean }>`/order`,
  updateOrderAutoRenewal: patch<
    any,
    { auto_renewal: boolean; catalog: number; type: number }
  >`/order`,

  getVolumes: get`/subvolumes`,
  modifySubvolume: patch<any, { id: number; quota_gb: number }>`/subvolume`,
  createSubvolume: post<any, { type: number; domain: any; init_quota_gb: number }>`/subvolume`,

  registerByOauth: post<
    { token?: string },
    { oauth_type: string; oauth_code: string; password: string }
  >`/user`,
  loginByOauth: post<
    { token: string },
    { oauth_type: string; oauth_code: string; inviter_code?: string }
  >`/login`,

  // instances
  createInstance: post<any, CreateInstanceReq>`/instance`,
  deleteInstance: del<any, DeleteInstanceReq>`/instance`,
  createSnapshot: patch<any, CreateSnapshotReq>`/instance`,
  getBriefMonitorData: get<GetBriefMonitorDataRes, GetBriefMonitorDataReq>`/mon/node`,
  getDetailMonitorData: get<any, GetMonitorDataReq>`/mon`,

  updateInstancePort: post<any, UpdateInstancePortReq>`/node_op`,

  getAttachments: get<ServerAttachmentInfo, GetAttachmentsReq>`/attachments`,
  updateAttachments: put<any, UpdateAttachmentsReq>`/attachments`,

  // auto deployment
  createDeployment: post<any, CreateDeploymentReq>`/deployment`,
  updateDeployment: put<any, UpdateDeploymentReq>`/deployment`,
  deleteDeployment: del<any, { res_id: string }>`/deployment`,
  getDeploymentBill: get<GetDeploymentBillRes, Pagi<{ res_id: string }>>`/deployment/bill`,

  // auto scaling
  createScalingSpec: post<any, any>`/scale_spec`,
  getScaleSpecList: get<GetScaleSpecListRes, GetScaleSpecListReq>`/scale_spec/list`,
  getScaleSpec: get<GetScaleSpecRes, GetScaleSpecReq>`/scale_spec`,
  updateScaleSpecRule: put<any, any>`/scale_spec/rule`,
  updateScaleReplicaLimit: put<any, any>`/scale_spec/replica_limit`,
  updateScaleRuntimes: put<any, any>`/scale_spec/runtimes`,
  updateScaleBehaviors: put<any, any>`/scale_spec/behaviors`,
  toggleScaleSpec: put<any, any>`/scale_spec/enabled`,
  deleteScaleSpec: del<any, any>`/scale_spec`,
  updateScaleMetrics: put<any, any>`/scale_spec/metrics`,
  getScaleMetricList: get<GetScaleMetricListRes, GetScaleMetricListReq>`/scale_metrics`,
  createScaleMetrics: post<any, any>`/scale_metrics`,

  // snapshot
  getItemConsume: get<GetItemConsumeRes, { type: number }>`/item_consume`,
  resumeSnapshot: patch<any, ResumeSnapshotReq>`/snapshot`,
  deleteSnapshot: del<any, DeleteSnapshotReq>`/snapshot`,

  // label
  createLabel: post<any, { source_id: number; subject: string; type: number }>`/label`,
  modifyLabel: patch<any, { id: number; subject: string; type: number }>`/label`,
  deleteLabel: del<any, { id: number }>`/label`,

  // mesh clustering
  createMesh: post<any, { name: string; domain: number; cluster_type: number }>`/mesh`,
  deleteMesh: del<any, DeleteMeshReq>`/mesh`,
  updateMesh: patch<any, UpdateMeshReq>`/mesh`,
  addEndpointToMesh: post<any, AddEndpointToMeshReq>`/mesh/endpoint`,
  deleteEndpointFromMesh: del<any, DeleteEndpointFromMeshReq>`/mesh/endpoint`,
  getMeshEndpointCandidates: get<
    GetMeshEndpointCandidatesRes,
    GetMeshEndpointCandidatesReq
  >`/mesh/endpoint/candidates`,

  // profile
  getEduVerifyStatus: get`/edu_verify`,
  eduVerify: post<any, EduVerifyReq>`/edu_verify`,
  // user ssh pubkey
  getUserSshPubKey: get<GetUserSshPubKeyRes, any>`/user/public_key`,
  addUserSshPubKey: post<any, AddUserSshPubKeyReq>`/user/public_key`,
  deleteUserSshPubKey: del<any, any>`/user/public_key`,

  // exchange rate
  getExchangeRate: get<
    GetExchangeRateRes,
    { credit_cent: number; pay_currency: number }
  >`/payment/exchange_rate/lookup`,

  // NAT
  getNATList: get<GetNATListRes, GetNATListReq>`/nats`,
  checkNATPort: get<any, { res_id: string; listen_port: number }>`/nat/port_mapping/check`,
}
